"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  InputAdornment,
  Grid,
  Fade,
  Divider,
} from "@mui/material";
import {
  SettingsOutlined,
  AttachMoneyOutlined,
  LayersOutlined,
  SaveOutlined,
} from "@mui/icons-material";
import httpClient from "../../utils/httpClient";
import { useSnackbar } from "notistack";

const ConvenienceForm = () => {
  const [convenience, setConvenience] = useState("");
  const [platform, setPlatform] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isChanged, setIsChanged] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = () => {
    setLoading(true);
    httpClient
      .put("/create-app-setting", {
        convenience_fee: convenience,
        platform_fee: platform,
      })
      .then((res) => {
        console.log("update app setting => ", res.data.data);
        enqueueSnackbar(res?.data?.message || "Settings updated successfully", {
          variant: "success",
        });
        setIsChanged(false);
      })
      .catch((err) => {
        console.log("update app setting error => ", err);
        enqueueSnackbar(
          err?.response?.data?.message || "Failed to update settings",
          { variant: "error" },
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setInitialLoading(true);
    httpClient
      .get("/get-app-setting")
      .then((res) => {
        console.log("get app setting => ", res.data.data);
        setConvenience(res.data?.data?.convenience_fee || 0);
        setPlatform(res.data?.data?.platform_fee || 0);
      })
      .catch((err) => {
        console.log("get app setting error => ", err);
        enqueueSnackbar("Failed to load settings", { variant: "error" });
      })
      .finally(() => {
        setInitialLoading(false);
      });
  }, []);

  const handleConvenienceChange = (e) => {
    setConvenience(e.target.value);
    setIsChanged(true);
  };

  const handlePlatformChange = (e) => {
    setPlatform(e.target.value);
    setIsChanged(true);
  };

  if (initialLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
        }}
      >
        <Fade in={true} unmountOnExit>
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress
              size={48}
              thickness={4}
              sx={{ color: "#3b82f6" }}
            />
            <Typography
              variant="body2"
              sx={{ mt: 2, color: "#64748b", fontWeight: 500 }}
            >
              Loading settings...
            </Typography>
          </Box>
        </Fade>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        p: 3,
        mt: 12,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 500,
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
            p: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <SettingsOutlined sx={{ color: "white", fontSize: 32 }} />
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "white", lineHeight: 1.2 }}
            >
              App Settings
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.8)", mt: 0.5 }}
            >
              Manage your application fees
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#64748b",
              mb: 3,
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <LayersOutlined fontSize="small" />
            Fee Configuration
          </Typography>

          <Grid container spacing={3}>
            {/* Convenience Fee */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Convenience Fee"
                variant="outlined"
                value={convenience}
                onChange={handleConvenienceChange}
                placeholder="Enter convenience fee"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyOutlined sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3b82f6",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#3b82f6",
                    fontWeight: 600,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: "#94a3b8", mt: 0.5, display: "block" }}
              >
                Transaction convenience fee percentage
              </Typography>
            </Grid>

            {/* Platform Fee */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Platform Fee"
                variant="outlined"
                value={platform}
                onChange={handlePlatformChange}
                placeholder="Enter platform fee"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyOutlined sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3b82f6",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#3b82f6",
                    fontWeight: 600,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: "#94a3b8", mt: 0.5, display: "block" }}
              >
                Platform usage fee
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading || !isChanged}
            startIcon={!loading && <SaveOutlined />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              py: 1.5,
              fontSize: "1rem",
              boxShadow: "none",
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
                background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
              },
              "&:disabled": {
                bgcolor: "#e2e8f0",
                color: "#94a3b8",
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>Saving...</span>
              </Box>
            ) : (
              "Save Changes"
            )}
          </Button>

          {/* Info Box */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: "#f1f5f9",
              borderRadius: 2,
              borderLeft: "4px solid #3b82f6",
            }}
          >
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              <strong>Note:</strong> Changes made here will affect all future
              transactions. Please review before saving.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConvenienceForm;
